import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet-async';
import { ShieldCheck, Mail, ArrowRight, Loader2 } from 'lucide-react';
import { EmailVerificationStep } from '../components/auth/EmailVerificationStep';

// Landing page for the "click to verify" link in the verification email:
// /verify-email?token=<uuid>. Verifies on mount and logs the user in, then
// redirects to the dashboard. With no token, the user enters their email and
// then types the code they already have (or resends one) — we do NOT auto-send
// a fresh code, which would invalidate the code already in their inbox.
const VerifyEmailPage: React.FC = () => {
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'verifying' | 'error' | 'form' | 'code'>(
    token ? 'verifying' : 'form'
  );
  const [email, setEmail] = useState('');
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true; // guard against React 18 StrictMode double-invoke
    (async () => {
      try {
        await verifyEmail(undefined, undefined, token);
        navigate('/dashboard');
      } catch {
        setStatus('error');
      }
    })();
  }, [token, verifyEmail, navigate]);

  const goToCodeEntry = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) setStatus('code');
  };

  return (
    <>
      <Helmet>
        <title>Verify your email — SaveDelete</title>
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-white px-4">
        <div className="w-full max-w-sm text-center">
          <Link to="/" className="inline-flex items-center group mb-8">
            <div className="bg-blue-600 p-2 rounded-lg group-hover:bg-blue-700 transition-colors">
              <span className="text-white font-bold text-xl">SD</span>
            </div>
            <span className="ml-3 text-2xl font-bold text-gray-900 tracking-tight">SaveDelete</span>
          </Link>

          {status === 'verifying' && (
            <div className="flex flex-col items-center">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
              <p className="mt-4 text-sm text-gray-600">Verifying your email…</p>
            </div>
          )}

          {status === 'code' && (
            <EmailVerificationStep
              email={email.trim()}
              onVerified={() => navigate('/dashboard')}
            />
          )}

          {(status === 'error' || status === 'form') && (
            <div className="space-y-6">
              <div>
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
                  <ShieldCheck className="h-6 w-6 text-blue-600" />
                </div>
                <h2 className="mt-4 text-2xl font-extrabold text-gray-900">
                  {status === 'error' ? 'That link didn’t work' : 'Verify your email'}
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  {status === 'error'
                    ? 'The link may have expired or already been used. Enter your email to continue.'
                    : 'Enter your email — then type the 6-digit code we sent you.'}
                </p>
              </div>

              <form onSubmit={goToCodeEntry} className="space-y-4">
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all"
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
                <p className="text-xs text-gray-500">
                  Already signed up?{' '}
                  <Link to="/login" className="underline">Log in instead</Link>.
                </p>
              </form>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VerifyEmailPage;
